import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { ApiError } from "../lib/api";
import {
  ADD_ANIMAL_SHEET_TITLE,
  ANIMAL_BIRTH_DATE_FIELD_LABEL,
  ANIMAL_NAME_FIELD_LABEL,
  ANIMAL_NOTES_FIELD_LABEL,
  ANIMAL_PADDOCK_FIELD_LABEL,
  ANIMAL_SPECIES_FIELD_LABEL,
  animalNameConflictMessage,
  COULD_NOT_SAVE_ERROR,
  editAnimalSheetTitle,
} from "../lib/recordsCopy";
import type { AnimalOut } from "../types";

export interface AnimalFormSheetFields {
  name: string;
  species: string | null;
  birth_date: string | null;
  paddock: string | null;
}

/** Sprint-016 F1 AN-01 (ui-redesign-direction-2026-07-11.md item 1, CD
 * binding condition 1) — `mode="edit"` submissions only, widening
 * `AnimalFormSheetFields` with `notes` (the field this sheet now also
 * carries, merged in from the retired inline Overview textarea). Kept as
 * its own type rather than adding `notes` to `AnimalFormSheetFields`
 * itself so `onAdd`'s (Add-mode) signature is unaffected — the `mode=
 * "add"` 4-field set stays exactly as-is, per the direction doc. */
export interface AnimalFormSheetEditFields extends AnimalFormSheetFields {
  notes: string | null;
}

export interface AnimalFormSheetProps {
  open: boolean;
  /** `"add"` (default) — the `AnimalsView` header's `"+ Add"` sheet.
   * `"edit"` — `AnimalDetailOverlay`'s admin-only "Edit" affordance,
   * pre-filled from `animal`. Same single-sheet, mode-driven shape as
   * `AddHealthNoteSheet` (design-spec-animal-cards-2026-07-10.md §5's own
   * "reuse the existing bottom-sheet/form-sheet component patterns"
   * instruction — this is the one shared sheet both callers use, rather
   * than two near-duplicate components. */
  mode?: "add" | "edit";
  /** Required when `mode === "edit"` — the animal being edited, to
   * pre-fill every field. */
  animal?: AnimalOut;
  /** design-spec §5 (CD blocking change #3) — the species combobox's
   * `<datalist>` suggestion values (illustrative common species UNION the
   * herd's own already-in-use freeform values — see
   * `lib/recordsCopy.ts::speciesDatalistOptions`). Any string is a valid
   * submission, including one not in this list. */
  speciesOptions: string[];
  onAdd?: (fields: AnimalFormSheetFields) => Promise<void>;
  onSave?: (fields: AnimalFormSheetEditFields) => Promise<void>;
  onCancel: () => void;
}

const SPECIES_DATALIST_ID = "animal-form-species-options";

/**
 * Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5) — the Animals
 * Add/Edit bottom sheet: name (required), species (freeform combobox, CD
 * blocking change #3 — never a closed `<select>`), birth date (optional
 * date input), paddock (optional freeform text). Same chrome/dismiss
 * conventions as every other sheet in this app (backdrop click, Escape,
 * Cancel all dismiss without saving) and the same synchronous-reset-on-
 * open discipline as `AddPersonSheet`/`AddHealthNoteSheet` — see
 * `AddPersonSheet.tsx`'s docstring for the sprint-005 C1 race-condition
 * writeup this pattern exists to avoid repeating.
 *
 * Notes stay OUT of `mode="add"` — a brand-new animal is created with the
 * four structural identity fields only, unchanged from the design spec's
 * §5 form. Sprint-016 F1 AN-01 (ui-redesign-direction-2026-07-11.md item 1,
 * ratified by CD sign-off `cd-signoff-ui-audit-2026-07-11.md` binding
 * condition 1) merges `notes` INTO `mode="edit"` as the last field,
 * retiring the second "Edit" affordance the Overview tab's inline textarea
 * used to be (`OverviewPanel` in `AnimalDetailOverlay.tsx` is now
 * read-only display + the unchanged "+ Add a note" CTA when empty — this
 * sheet is the one place notes are ever written).
 */
export function AnimalFormSheet({
  open,
  mode = "add",
  animal,
  speciesOptions,
  onAdd,
  onSave,
  onCancel,
}: AnimalFormSheetProps) {
  const [name, setName] = useState(mode === "edit" ? (animal?.name ?? "") : "");
  const [species, setSpecies] = useState(mode === "edit" ? (animal?.species ?? "") : "");
  const [birthDate, setBirthDate] = useState(mode === "edit" ? (animal?.birth_date ?? "") : "");
  const [paddock, setPaddock] = useState(mode === "edit" ? (animal?.paddock ?? "") : "");
  const [notes, setNotes] = useState(mode === "edit" ? (animal?.notes ?? "") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset SYNCHRONOUSLY on the closed->open transition — see
  // `AddPersonSheet.tsx`'s docstring for why this must never be a
  // post-commit `useEffect` (a fast fill/fast test automation can race an
  // async reset and have its input silently wiped).
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setName(mode === "edit" ? (animal?.name ?? "") : "");
    setSpecies(mode === "edit" ? (animal?.species ?? "") : "");
    setBirthDate(mode === "edit" ? (animal?.birth_date ?? "") : "");
    setPaddock(mode === "edit" ? (animal?.paddock ?? "") : "");
    setNotes(mode === "edit" ? (animal?.notes ?? "") : "");
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: nameRef,
  });

  if (!open) return null;

  const trimmedName = name.trim();

  async function submit() {
    if (!trimmedName) return;
    setSaving(true);
    setError(null);
    const fields: AnimalFormSheetFields = {
      name: trimmedName,
      species: species.trim() ? species.trim() : null,
      birth_date: birthDate.trim() ? birthDate.trim() : null,
      paddock: paddock.trim() ? paddock.trim() : null,
    };
    try {
      if (mode === "edit") {
        await onSave?.({ ...fields, notes: notes.trim() ? notes.trim() : null });
      } else {
        await onAdd?.(fields);
      }
    } catch (err) {
      // design-spec §5's "Inline validation / error states" — the
      // specific 409 name-conflict message, never the generic fallback,
      // for this one case.
      if (err instanceof ApiError && err.status === 409) {
        setError(animalNameConflictMessage(trimmedName));
      } else {
        setError(err instanceof Error ? err.message : COULD_NOT_SAVE_ERROR);
      }
    } finally {
      setSaving(false);
    }
  }

  const titleId = "animal-form-sheet-title";

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id={titleId}>
          {mode === "edit" ? editAnimalSheetTitle(animal?.name ?? "") : ADD_ANIMAL_SHEET_TITLE}
        </h2>

        <label class="admin-field-label" for="animal-form-name" style={{ marginTop: 0 }}>
          {ANIMAL_NAME_FIELD_LABEL}
        </label>
        <input
          ref={nameRef}
          id="animal-form-name"
          type="text"
          class="pick-input"
          value={name}
          required
          aria-required="true"
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="animal-form-species">
          {ANIMAL_SPECIES_FIELD_LABEL}
        </label>
        <input
          id="animal-form-species"
          type="text"
          class="pick-input"
          list={SPECIES_DATALIST_ID}
          value={species}
          onInput={(e) => setSpecies((e.target as HTMLInputElement).value)}
        />
        <datalist id={SPECIES_DATALIST_ID}>
          {speciesOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>

        <label class="admin-field-label" for="animal-form-birth-date">
          {ANIMAL_BIRTH_DATE_FIELD_LABEL}
        </label>
        <input
          id="animal-form-birth-date"
          type="date"
          class="pick-input"
          max={new Date().toISOString().slice(0, 10)}
          value={birthDate}
          onInput={(e) => setBirthDate((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="animal-form-paddock">
          {ANIMAL_PADDOCK_FIELD_LABEL}
        </label>
        <input
          id="animal-form-paddock"
          type="text"
          class="pick-input"
          value={paddock}
          onInput={(e) => setPaddock((e.target as HTMLInputElement).value)}
        />

        {mode === "edit" ? (
          <>
            <label class="admin-field-label" for="animal-form-notes">
              {ANIMAL_NOTES_FIELD_LABEL}
            </label>
            <textarea
              id="animal-form-notes"
              class="pick-input admin-textarea"
              value={notes}
              onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
            />
          </>
        ) : null}

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!trimmedName || saving}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Add"}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
